import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowRight, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail, TOKEN_KEY } from "@/lib/apiClient";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { email: email.trim().toLowerCase(), password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      navigate("/admin/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#050B14] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-12">
          <div className="w-8 h-8 border border-gold flex items-center justify-center">
            <div className="w-2 h-2 bg-gold rotate-45" />
          </div>
          <span className="font-heading text-lg text-white">Direct<span className="text-gold">Fund</span>Cap</span>
        </Link>

        <div className="bg-[#0A1326] border border-white/10 p-8 md:p-10">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-4 h-4 text-gold" />
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-gold">Internal Access</span>
          </div>
          <h1 className="font-heading text-3xl text-white mb-2">Admin Sign-in</h1>
          <p className="text-white/50 text-sm mb-8">Funding desk and operations only.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
            <label className="block">
              <span className="text-xs tracking-[0.18em] uppercase text-white/60 mb-2 block">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email"
                className="w-full bg-[#050B14] border border-white/10 focus:border-gold focus:outline-none text-white px-4 py-3 transition-colors"
                placeholder="admin@directfundcap.com"
              />
            </label>
            <label className="block">
              <span className="text-xs tracking-[0.18em] uppercase text-white/60 mb-2 block">Password</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password"
                className="w-full bg-[#050B14] border border-white/10 focus:border-gold focus:outline-none text-white px-4 py-3 transition-colors"
                placeholder="••••••••"
              />
            </label>

            {error && (
              <div data-testid="admin-login-error" className="border border-red-500/40 bg-red-500/5 text-red-400 text-sm px-4 py-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-submit"
              className="w-full bg-gold hover:bg-gold-400 disabled:opacity-60 text-navy-950 font-semibold px-6 py-3.5 inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>

        <div className="text-center mt-8">
          <Link to="/" className="text-xs tracking-wider uppercase text-white/40 hover:text-gold">← Back to site</Link>
        </div>
      </div>
    </div>
  );
}
